import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import moment from 'moment';
import i18next from 'i18next';

import {CheckboxInput} from '../CheckboxInput';
import {SelectInput} from '../SelectInput';
import { TimePickerInput } from "../TimePicker";

const validate = values => {
    const errors = {};

    if (!values.role) {
        errors.role = i18next.t('messages.please-select-distro');
    }

    if (!values.timezone) {
        errors.timezone = i18next.t('messages.please-select-timezone');
    }

    if (!values.dayOfWeek) {
        errors.dayOfWeek = i18next.t('messages.please-select-day-of-week');
    }

    if (!values.timeOfDay) {
        errors.timeOfDay = i18next.t('messages.please-enter-time');
    }

    return errors;
}

const AVAILABLE_FEATURE_FLAGS = {
    "ALL_DEVICES_REPORT": [{name: 'Include Online/Offline Graph', value: 'generate_online_graph', default: true}],
    "ALL_DEVICES_30_DAY_REPORT": [{name: 'Include Online/Offline Graph', value: 'generate_online_graph', default: true}]
};

const getDayOfTheWeekLabel = (dayOfWeek) => {
    switch (dayOfWeek.toLowerCase()) {
        case ('monday'): {
            return moment.weekdays(1);
        }
        case ('tuesday'): {
            return moment.weekdays(2);
        }
        case ('wednesday'): {
            return moment.weekdays(3);
        }
        case ('thursday'): {
            return moment.weekdays(4);
        }
        case ('friday'): {
            return moment.weekdays(5);
        }
        case ('saturday'): {
            return moment.weekdays(6);
        }
        case ('sunday'): {
            return moment.weekdays(0);
        }
    }
};

const ReportForm = ({ form, initialize, handleSubmit, report, roleOptions, timezones, days }) => {
    const [advancedFlagOptions, setAdvancedFlagOptions] = useState([]);

    useEffect(() => {
        moment.locale(i18next.language);

        let reportEnabled = (report?.id ?? -1) >= 0;

        const featureFlagsForType = AVAILABLE_FEATURE_FLAGS[report.reportType] ?? [];
        setAdvancedFlagOptions(featureFlagsForType);

        let featureFlagSettings = {};

        featureFlagsForType.forEach((featureFlag) => {
            if (report.featureFlags && featureFlag.value in report.featureFlags) {
                featureFlagSettings[featureFlag.value] = report.featureFlags[featureFlag.value];
            } else {
                featureFlagSettings[featureFlag.value] = featureFlag.default;
            }
        });

        initialize({
            reportType: report.reportType,
            active: reportEnabled,
            role: reportEnabled ? { value: report.role.id, label: report.role.name } : null,
            timezone: reportEnabled ? { value: report.timezone, label: report.timezone } : null,
            dayOfWeek: reportEnabled ? { value: report.dayOfWeek, label: getDayOfTheWeekLabel(report.dayOfWeek) } : { label: moment.weekdays(1), value: "monday" },
            timeOfDay: reportEnabled ? (report.timeOfDay ?? "07:00") : "07:00",
            advanced: featureFlagSettings,
        });
    }, [initialize, report]);

    const isReportEnabled = useMemo(() => {
        let reportForm = report.reportType;
        return form && form[reportForm] && form[reportForm].values && form[reportForm].values.active;
    }, [form, report]);

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <div>
                    <Field className="inline" name="active" component={CheckboxInput} type="checkbox" label={i18next.t("enabled")} />
                </div>
                <div className="row">
                    <div className="form-group col-sm-12 col-md-6 col-lg-6">
                        <label>{i18next.t("pages.scheduled-reports.distribution-group")}</label>
                        <Field name="role" options={roleOptions} component={SelectInput} isDisabled={!isReportEnabled} />
                    </div>

                    <div className="form-group col-sm-12 col-md-6 col-lg-6">
                        <label>{i18next.t("timezone")}</label>
                        <Field name="timezone" options={timezones} component={SelectInput} isDisabled={!isReportEnabled} />
                    </div>

                    <div className="form-group col-sm-12 col-md-6 col-lg-6">
                        <label>{i18next.t("pages.scheduled-reports.day-of-week")}</label>
                        <Field name="dayOfWeek" options={days} component={SelectInput} isDisabled={!isReportEnabled} />
                    </div>

                    <div className="form-group col-sm-12 col-md-6 col-lg-6">
                        <label>{i18next.t("pages.scheduled-reports.time-of-day")}</label>
                        <Field name="timeOfDay" type="time" component={TimePickerInput} valueType="ampm" isDisabled={!isReportEnabled} />
                    </div>

                    {advancedFlagOptions?.length > 0 && (
                        <div className="form-group col-sm-12 col-md-6 col-lg-6">
                            <label>Advanced Features</label>
                            {advancedFlagOptions.map((option) => (
                                <Field
                                    key={option.value}
                                    className="inline"
                                    name={`advanced[${option.value}]`}
                                    component={CheckboxInput}
                                    type="checkbox"
                                    label={option.name}
                                    isDisabled={!isReportEnabled}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <hr />

                <ul className="list-inline clearfix">
                    <li>
                        <button type="submit" className="btn btn-primary btn-block">
                            {i18next.t("form-labels.save")}
                        </button>
                    </li>
                </ul>
            </form>
        </div>
    );
};

export default reduxForm({validate})(connect(state => ({ form: state.form }))(ReportForm));